// ─────────────────────────────────────────────────────────────────────────────
// EnrichExternalReferencesDialogComponent
// ─────────────────────────────────────────────────────────────────────────────
// Opened from a company-matched order to register the AI-parsed codes of its
// semantically-matched lines as customer-scoped external references. Lists one
// toggle row per (code, matched SKU) — default ON, since the suggestion is the
// intended action here (the opposite of the noisy email-enrich default).
//
// Codes already saved for this customer+SKU are shown disabled and excluded from
// creation: the create endpoint has no idempotent skip and raises on a duplicate,
// so excluding them client-side is functional, not cosmetic.
//
// On Apply, one external reference is created per enabled row via the existing
// service. A single per-row failure is non-fatal — the remaining creates still
// fire and the dialog still closes. Applying with zero enabled rows is a no-op.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { SharedModule } from 'src/app/shared/shared.module';
import { SharedDialogComponent } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { CommonService } from 'src/app/shared/common.service';
import { ExternalReferenceService } from 'src/app/features/knowledge-base/services/external-reference.service';

export interface IEnrichCodeInput {
  code: string;
  sku: string;
  productName?: string;
}

export interface IEnrichExternalReferencesDialogData {
  /** Matched company id (wl.company) — the reference owner. */
  customerId: number;
  /** Optional company name, shown in the dialog copy. */
  companyName?: string;
  /** Aggregated from hybrid_reranked lines: one entry per (code, matched product). */
  codes: IEnrichCodeInput[];
  /** Lowercased "sku|code" keys already stored for this customer — excluded from creation. */
  savedKeys?: Set<string>;
}

interface ICodeRow {
  code: string;
  sku: string;
  productName?: string;
  enabled: boolean;
  alreadySaved: boolean;
}

@Component({
  selector: 'app-enrich-external-references-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SharedModule,
    SharedDialogComponent,
  ],
  templateUrl: './enrich-external-references-dialog.component.html',
  styleUrls: ['./enrich-external-references-dialog.component.scss'],
})
export class EnrichExternalReferencesDialogComponent implements OnInit {

  rows: ICodeRow[] = [];
  isSaving = false;

  constructor(
    public dialogRef: MatDialogRef<EnrichExternalReferencesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IEnrichExternalReferencesDialogData,
    private externalReferenceService: ExternalReferenceService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    // De-duplicate on lowercased "sku|code", drop blanks. A row already saved for
    // this customer is disabled and excluded; new codes default ON.
    const saved = this.data?.savedKeys ?? new Set<string>();
    const seen = new Set<string>();
    this.rows = (this.data?.codes ?? [])
      .filter((c) => {
        const key = `${c.sku}|${c.code}`.toLowerCase();
        if (!c.code || !c.sku || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((c) => {
        const alreadySaved = saved.has(`${c.sku}|${c.code}`.toLowerCase());
        return { ...c, alreadySaved, enabled: !alreadySaved };
      });
  }

  get selectableCount(): number {
    return this.rows.filter((row) => !row.alreadySaved).length;
  }

  get enabledCount(): number {
    return this.rows.filter((row) => row.enabled && !row.alreadySaved).length;
  }

  // ── Actions ──────────────────────────────────────────────────────────────────

  apply(): void {
    if (this.isSaving) return;

    const toCreate = this.rows.filter((row) => row.enabled && !row.alreadySaved);
    if (toCreate.length === 0) {
      this.dialogRef.close(false);
      return;
    }

    this.isSaving = true;
    const creates = toCreate.map((row) =>
      this.externalReferenceService
        .createExternalReference({
          sku: row.sku,
          customer_id: this.data.customerId,
          reference_code: row.code,
          source: 'rule',
        })
        .pipe(catchError(() => of(null))),
    );

    forkJoin(creates)
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe((results) => {
        const failed = results.filter((res) => res === null).length;
        this.commonService.showToast(
          this.translateService.instant(
            failed > 0
              ? 'quotations_and_orders.enrich_external_references.partial_error'
              : 'quotations_and_orders.enrich_external_references.success',
          ),
          failed > 0 ? 'ERROR' : 'SUCCESS',
        );
        this.dialogRef.close(true);
      });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
